import clsx from 'clsx';
import { FileText, ListChecks, MessageSquare, Receipt, Search } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearch } from '@/api';
import type { SearchHit } from '@/api/types';
import { useDebounced } from '@/lib/useDebounced';

const icon: Record<SearchHit['kind'], typeof FileText> = { task: ListChecks, thread: MessageSquare, document: FileText, invoice: Receipt };
const group: Record<SearchHit['kind'], string> = { task: 'Задачи', thread: 'Переписка', document: 'Файлы', invoice: 'Счета' };

/** Search across tasks, messages, files and invoices — the way to find things when there are hundreds of them. */
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState(0);
  // The request goes out ~200 ms after the last key, not on each one.
  const asked = useDebounced(q, 200);
  const { data: hits, isFetching } = useSearch(asked);
  const settled = asked === q && !isFetching;
  const navigate = useNavigate();
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setQ('');
      setCursor(0);
      setTimeout(() => input.current?.focus(), 0);
    }
  }, [open]);
  useEffect(() => setCursor(0), [hits]);

  if (!open) return null;
  const list = asked.trim().length >= 2 ? (hits ?? []) : [];
  const go = (h?: SearchHit) => {
    if (!h) return;
    onClose();
    navigate(h.to);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center px-4 pt-[12vh]" role="dialog" aria-modal="true" aria-label="Поиск">
      <div className="absolute inset-0 bg-ink/30 backdrop-blur-[2px]" onClick={onClose} />
      <div className="relative w-full max-w-xl overflow-hidden rounded-lg bg-canvas shadow-pop">
        <div className="flex items-center gap-3 border-b border-line px-4">
          <Search className="size-4 text-muted" />
          <input
            ref={input}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') onClose();
              if (e.key === 'ArrowDown') setCursor((c) => Math.min(c + 1, list.length - 1));
              if (e.key === 'ArrowUp') setCursor((c) => Math.max(c - 1, 0));
              if (e.key === 'Enter') go(list[cursor]);
            }}
            placeholder="Задача, слово из переписки, файл, номер счёта"
            className="h-12 flex-1 bg-transparent text-md placeholder:text-faint focus:outline-none"
            aria-label="Что найти"
          />
          <span className="kbd">Esc</span>
        </div>
        <div className="max-h-palette overflow-y-auto p-2">
          {q.trim().length < 2 ? (
            <p className="px-3 py-6 text-center text-sm text-muted">Начните печатать — ищем по задачам, всей переписке, файлам и счетам.</p>
          ) : !settled && list.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted" role="status">
              Ищем…
            </p>
          ) : list.length === 0 && settled ? (
            <p className="px-3 py-6 text-center text-sm text-muted">Ничего не нашли по «{q}». Попробуйте другое слово.</p>
          ) : (
            list.map((h, i) => {
              const Icon = icon[h.kind];
              const header = i === 0 || list[i - 1].kind !== h.kind;
              return (
                <div key={`${h.kind}-${h.id}`}>
                  {header && <div className="px-3 pb-1 pt-3 text-xs text-faint">{group[h.kind]}</div>}
                  <button
                    onMouseEnter={() => setCursor(i)}
                    onClick={() => go(h)}
                    className={clsx('flex w-full items-center gap-3 rounded px-3 py-2 text-left', i === cursor && 'bg-sunken')}
                  >
                    <Icon className="size-4 shrink-0 text-muted" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm">{h.title}</span>
                      <span className="block truncate text-xs text-muted">{h.hint}</span>
                    </span>
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
